/* Custom cart drawer – Cevora
 * - Opens instantly when anything adds to the cart (theme, Kaching bundles, sticky bar, upsell).
 * - All cart HTML is rendered by sections/custom-cart.liquid; this file only swaps it in.
 */
(() => {
  const root = document.getElementById('CustomCart');
  if (!root || root.dataset.ccReady) return;
  root.dataset.ccReady = 'true';

  const SECTION_ID = 'custom-cart';
  const BASE = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  const errorEl = root.querySelector('.cc__error');
  let pending = 0;
  let refreshTimer = null;
  let lastFocus = null;

  // Stop the theme's built-in drawer/popup from opening (it is also hidden in CSS)
  const silenceThemeCart = () => {
    document.querySelectorAll('cart-drawer, cart-notification').forEach((el) => {
      el.open = () => {};
    });
  };
  silenceThemeCart();

  const sectionIds = () => {
    const ids = [SECTION_ID];
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  };

  const setLoading = (on) => {
    pending = Math.max(0, pending + (on ? 1 : -1));
    root.classList.toggle('is-loading', pending > 0);
  };

  const showError = (message) => {
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  };

  const open = () => {
    if (root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cc-lock');
    document.body.classList.remove('overflow-hidden');
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
    root.querySelector('.cc__panel').focus({ preventScroll: true });
  };

  const close = () => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cc-lock');
    document.body.classList.remove('overflow-hidden');
    showError('');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const render = (sections) => {
    if (!sections) return;
    if (sections[SECTION_ID]) {
      const next = parse(sections[SECTION_ID]).getElementById('CC-Content');
      const current = document.getElementById('CC-Content');
      if (next && current) {
        const scroller = current.querySelector('.cc__scroll');
        const scrollTop = scroller ? scroller.scrollTop : 0;
        current.innerHTML = next.innerHTML;
        const newScroller = current.querySelector('.cc__scroll');
        if (newScroller) newScroller.scrollTop = scrollTop;
      }
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const wrapper = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (wrapper) bubble.innerHTML = wrapper.innerHTML;
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await nativeFetch(`${BASE}?sections=${sectionIds().join(',')}`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) render(await res.json());
    } catch (e) {
      // Network hiccup: the next cart action will refresh again
    } finally {
      setLoading(false);
    }
  };

  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 150);
  };

  // Sends a cart change and renders the returned HTML in the same request
  const send = async (endpoint, body) => {
    showError('');
    setLoading(true);
    try {
      const res = await nativeFetch(`${BASE}cart/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) {
        showError(data.description || data.message || 'Something went wrong. Please try again.');
        await refresh();
        return;
      }
      if (data.sections) {
        render(data.sections);
      } else {
        await refresh();
      }
      document.dispatchEvent(new CustomEvent('custom-cart:updated'));
    } catch (e) {
      showError('Connection problem. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Changing a bundle's main product also changes/removes its Kaching free gifts
  const changeLine = (itemEl, newQty) => {
    const updates = { [itemEl.dataset.key]: newQty };
    const bundle = itemEl.dataset.bundle;
    const oldQty = Number(itemEl.dataset.qty) || 1;
    if (bundle && itemEl.dataset.gift !== 'true') {
      root.querySelectorAll('.cc-item[data-gift="true"]').forEach((gift) => {
        if (gift.dataset.bundle !== bundle) return;
        updates[gift.dataset.key] =
          newQty === 0 ? 0 : Math.max(1, Math.round((Number(gift.dataset.qty) * newQty) / oldQty));
      });
    }
    send('update.js', { updates });
  };

  root.addEventListener('click', (event) => {
    const target = event.target;

    if (target.closest('[data-cc-close]')) {
      event.preventDefault();
      close();
      return;
    }
    if (pending > 0) return;

    const removeBtn = target.closest('[data-cc-remove]');
    if (removeBtn) {
      changeLine(removeBtn.closest('.cc-item'), 0);
      return;
    }

    const qtyBtn = target.closest('[data-cc-qty]');
    if (qtyBtn && !qtyBtn.disabled) {
      const item = qtyBtn.closest('.cc-item');
      const nextQty = Math.max(0, Number(item.dataset.qty) + Number(qtyBtn.dataset.ccQty));
      changeLine(item, nextQty);
      return;
    }

    const addBtn = target.closest('[data-cc-add]');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      send('add.js', { items: [{ id: Number(addBtn.dataset.ccAdd), quantity: 1 }] });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-open')) close();
  });

  // Header cart icon opens this drawer instead of the /cart page or the theme drawer
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('#cart-icon-bubble, [data-cc-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      silenceThemeCart();
      open();
    },
    true
  );

  // Watch cart requests made by the theme and other apps
  const onCartRequest = (url, request) => {
    if (!CART_MUTATION.test(url)) return;
    const isAdd = url.includes('/cart/add');
    if (isAdd) {
      silenceThemeCart();
      showError('');
      open();
      setLoading(true);
    }
    request
      .then((res) => {
        if (isAdd && res && res.ok === false && res.clone) {
          res.clone().json().then((d) => showError(d.description || d.message), () => {});
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isAdd) setLoading(false);
        scheduleRefresh();
      });
  };

  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    try {
      const url = input instanceof Request ? input.url : String(input);
      onCartRequest(url, request);
    } catch (e) {}
    return request;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._ccUrl = String(url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this._ccUrl && CART_MUTATION.test(this._ccUrl)) {
      const xhr = this;
      onCartRequest(
        this._ccUrl,
        new Promise((resolve) => {
          xhr.addEventListener('loadend', () => resolve({ ok: xhr.status >= 200 && xhr.status < 300 }));
        })
      );
    }
    return xhrSend.apply(this, arguments);
  };

  // Coming back with the browser Back button can show an old cart
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
})();
